/**
 * settingsConfirmacoes.js — os parâmetros que NÃO mudam com um clique
 * CI Sistemas de Informática Ltda. — AcessoiD (Onda 101)
 *
 * Três parâmetros da seção Operação mudam o comportamento da plataforma inteira, e
 * até a Onda 100 mudavam com um clique — um `select` que gravava no `onChange`, um
 * interruptor que gravava no toque. Esbarrar neles bastava. E, como o serviço das
 * câmeras lê as configurações a cada quadro, o efeito era imediato.
 *
 * Cada entrada recebe o valor atual e o pretendido, e devolve o que o diálogo diz:
 * o que acontece, em palavras de operação, e não "tem certeza?". Uma confirmação
 * que não diz a consequência é só um segundo clique.
 */
const NOME_DA_VERTICAL = {
  estacionamento: 'Estacionamento Convencional',
  condominio_residencial: 'Condomínio Residencial',
  condominio_corporativo: 'Condomínio Corporativo',
  clube_social: 'Clube Social (integração externa)',
};

export const CONFIRMACOES = {
  system_vertical: (de, para) => ({
    titulo: 'Mudar o tipo de operação?',
    perigo: true,
    rotulo: 'Mudar o tipo de operação',
    paragrafos: [
      `De "${NOME_DA_VERTICAL[de] || de || 'não definido'}" para "${NOME_DA_VERTICAL[para] || para}".`,
      'Isso muda o perfil da plataforma inteira, para todos os usuários: quais módulos e menus existem, se há cobrança e tarifas, e o que acontece quando uma câmera lê uma placa. Vale na hora.',
      'Nada é apagado — tickets, cadastros e configurações continuam no banco. Mas a operação em curso deixa de funcionar como funcionava. Só mude numa instalação nova, ou com a operação parada.',
    ],
  }),

  // Onda 154
  cobranca_habilitada: (de, para) => (para === 'true' ? {
    titulo: 'Habilitar a cobrança de ticket?',
    rotulo: 'Habilitar a cobrança',
    paragrafos: [
      'Os módulos de cobrança passam a aparecer no menu e na Central de Ajuda desta instalação, para os perfis que os alcançam: Tickets, Caixa, Tarifas, cadastros de clientes, mensalistas, credenciados e convênios, Reconciliação, Quarentena, Inventário de Pátio, relatórios e o app da guarita.',
      'Os módulos próprios da vertical continuam como estão. Vale na hora, para todos — quem está com a plataforma aberta vê a mudança ao recarregar a página.',
    ],
  } : {
    titulo: 'Desabilitar a cobrança de ticket?',
    perigo: true,
    rotulo: 'Desabilitar a cobrança',
    paragrafos: [
      'Os módulos de cobrança saem do menu e da Central de Ajuda, e as rotas deles passam a recusar acesso.',
      'Nada é apagado: tickets, pagamentos, tarifas e cadastros continuam no banco e voltam a aparecer se a cobrança for habilitada de novo.',
    ],
  }),

  parallel_mode: (de, para) => (para === 'true' ? {
    titulo: 'Ligar o Modo de Operação Paralela?',
    rotulo: 'Ligar o modo paralelo',
    paragrafos: [
      'A cancela de saída passa a ser liberada automaticamente para veículos avulsos, sem exigir pagamento no caixa. O valor é calculado e registrado como baixa automática.',
      'É o modo do período de transição, com o sistema legado ainda cobrando.',
    ],
  } : {
    titulo: 'Desligar o Modo de Operação Paralela?',
    perigo: true,
    rotulo: 'Desligar o modo paralelo',
    paragrafos: [
      'A plataforma deixa de dar baixa automática nos veículos avulsos: a saída passa a depender do pagamento registrado no caixa.',
      'Esta é a virada. Só desligue com o caixa aberto e operando, a equipe avisada e a cancela de saída integrada — um veículo que não pagou aqui não é liberado.',
    ],
  }),

  single_camera_mode: (de, para) => (para === 'true' ? {
    titulo: 'Ligar o Modo Câmera Única?',
    perigo: true,
    rotulo: 'Ligar o modo câmera única',
    paragrafos: [
      'Uma única câmera passa a decidir entrada e saída pelo estado do ticket: placa sem ticket ativo é ENTRADA; placa com ticket ativo é SAÍDA, com baixa automática.',
      'Não ligue numa instalação com câmeras separadas de entrada e de saída: a câmera de entrada passaria a fechar o ticket de quem manobra diante dela.',
    ],
  } : {
    titulo: 'Desligar o Modo Câmera Única?',
    rotulo: 'Desligar o modo câmera única',
    paragrafos: [
      'Cada câmera volta a valer pelo tipo cadastrado: a de entrada só abre ticket, a de saída só fecha.',
      'Numa instalação com uma câmera só, os veículos deixam de ter a saída registrada.',
    ],
  }),
};

export const pedeConfirmacao = (chave) => Object.prototype.hasOwnProperty.call(CONFIRMACOES, chave);
